import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import HeroVideo from "@/components/HeroVideo";
import { HERO_VIDEOS } from "@/data/backgroundVideos";
import {
  DollarSign,
  Zap,
  Bot,
  Building2,
  Users,
  Award,
  CheckCircle2,
  ArrowRight,
  ExternalLink,
  Tv,
  Radio,
  Globe,
  TrendingUp,
  Megaphone,
  Play,
  Calendar,
  MessageSquare,
  FileText,
  CreditCard,
  BarChart3,
  Search,
  Link2,
  MapPin,
  Eye,
  Shield,
  Rocket,
  Brain,
  Target,
  Repeat,
  Smartphone,
  Monitor,
  Volume2,
  Send,
  Mail,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ListingBenefitsSection from "@/components/ListingBenefitsSection";

export default function GetFundedPage() {
  const whatYouGet = [
    { icon: Award, title: "Best of Best Directory Listing", desc: "Premium visibility in our AI-powered directory" },
    { icon: Bot, title: "Your Own AI Business Hero™", desc: "24/7 digital spokesperson and growth engine" },
    { icon: Zap, title: "LocalBest.ai Platform", desc: "Complete business growth and automation system" },
    { icon: Users, title: "AI Horse-Powered CRM", desc: "Smart customer relationship management" },
    { icon: Building2, title: "BDC-X Business Dev Center", desc: "Lead generation and sales automation" },
    { icon: DollarSign, title: "Up to $120,000 Co-Op Funding", desc: "Growth capital for marketing & operations" },
  ];

  const heroCapabilities = [
    {
      category: "Marketing & Promotion",
      icon: Megaphone,
      color: "#7C3AED",
      items: [
        "Promote your business inside the Best of Best Directory",
        "Deploy your brand across TV, radio, social, and ads",
        "Publish social posts, blogs, and content",
        "Manage your reputation and reviews",
        "Act as your digital announcer and brand ambassador",
      ],
    },
    {
      category: "Sales & Lead Generation",
      icon: TrendingUp,
      color: "#00E5FF",
      items: [
        "Answer questions about your services",
        "Qualify leads automatically",
        "Book appointments on your calendar",
        "Confirm appointments and send reminders",
        "Manage your sales pipeline",
        "Send proposals, contracts, invoices, and payment links",
        "Close deals automatically",
      ],
    },
    {
      category: "Client Experience & Operations",
      icon: Users,
      color: "#F59E0B",
      items: [
        "Onboard new clients",
        "Run intake forms for ads, services, and orders",
        "Manage email and SMS communication",
        "Track every lead and conversation",
        "Sync with your calendar",
        "Route tasks to your team",
      ],
    },
    {
      category: "Reporting & Growth",
      icon: BarChart3,
      color: "#10B981",
      items: [
        "Generate daily, weekly, monthly, quarterly, and yearly reports",
        "Track ROI and performance",
        "Optimize campaigns automatically",
        "Recommend next actions",
      ],
    },
  ];

  const promotionChannels = [
    { icon: Globe, label: "Directory" },
    { icon: Tv, label: "TV" },
    { icon: Radio, label: "Radio" },
    { icon: Megaphone, label: "Meta Ads" },
    { icon: TrendingUp, label: "Google Ads" },
    { icon: Play, label: "TikTok" },
    { icon: Users, label: "Social Platforms" },
  ];

  return (
    <div className="page-container min-h-screen" data-testid="get-funded-page">
      {/* Hero Section */}
      <section className="py-8 md:py-12 hero-pattern relative overflow-visible">
        <div className="hero-glow top-1/4 left-1/4 opacity-60" />
        <div className="hero-glow bottom-1/4 right-1/4 opacity-40" style={{ background: "radial-gradient(circle, rgba(245, 158, 11, 0.15) 0%, transparent 70%)" }} />
        
        {/* Gold Horse - LEFT - Desktop */}
        <div className="absolute left-0 bottom-[-100px] w-[350px] h-[500px] pointer-events-none z-0 hidden lg:block">
          <img 
            src="https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/c1aj8flo_Untitled%20design%20%2836%29.gif"
            alt="AI Horse Hero Gold"
            className="w-full h-full object-contain opacity-90"
          />
        </div>
        
        {/* Dark Horse - RIGHT - Desktop */}
        <div className="absolute right-0 bottom-[-100px] w-[350px] h-[500px] pointer-events-none z-0 hidden lg:block">
          <img 
            src="https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/e002ywsd_Untitled%20design%20%2834%29.gif"
            alt="AI Horse Hero Dark"
            className="w-full h-full object-contain opacity-90"
          />
        </div>
        
        {/* Gold Horse - LEFT - Mobile */}
        <div className="absolute left-[-20px] bottom-[-60px] w-[140px] h-[200px] pointer-events-none block lg:hidden" style={{ zIndex: 0 }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/c1aj8flo_Untitled%20design%20%2836%29.gif"
            alt="AI Horse Hero Gold"
            className="w-full h-full object-contain opacity-70"
          />
        </div>
        
        {/* Dark Horse - RIGHT - Mobile */}
        <div className="absolute right-[-20px] bottom-[-60px] w-[140px] h-[200px] pointer-events-none block lg:hidden" style={{ zIndex: 0 }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/e002ywsd_Untitled%20design%20%2834%29.gif"
            alt="AI Horse Hero Dark"
            className="w-full h-full object-contain opacity-70"
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 mb-6">
              <DollarSign className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm text-[#F59E0B] font-medium">
                Co-Marketing & Up to $120,000 Co-Op Funding for Qualified Businesses
              </span>
            </div>
            
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-['Outfit'] text-white mb-6">
              Get Listed. <span className="gradient-text">Get Promoted.</span> Grow Faster.
            </h1>
            
            <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-white/90 mb-8">
              Launch Your Best of Best Listing Funnel — and Get Help Promoting It.
            </h2>

            {/* Hero Video — sits between horses, under headline */}
            <div className="mb-8">
              <HeroVideo
                src={HERO_VIDEOS.ownTheNumberOne}
                testid="grow-business-hero-video"
              />
            </div>

            <div className="text-[#94A3B8] text-sm md:text-base max-w-2xl mx-auto mb-6 leading-relaxed">
              <p className="mb-3">
                Qualified businesses may be eligible for <span className="text-white font-medium">co-marketing support</span>,
                <span className="text-white font-medium"> promotional credits</span>, or
                <span className="text-[#F59E0B] font-semibold"> up to $120,000 in partnership-based co-op funding</span> to help launch
                their Best of Best Listing Funnel, activate automation, and run paid ads.
              </p>
              <p className="text-xs text-[#64748B] italic">
                Funding is not guaranteed for every applicant. Eligibility and amounts vary by business, plan, and partnership terms.
              </p>
            </div>
            
            <div className="flex flex-col items-center gap-2 mb-8">
              <p className="text-[#E2E8F0] text-base font-medium">
                Built to help you get listed faster, capture more leads, and stay visible everywhere.
              </p>
              <p className="text-[#00E5FF] text-lg font-bold">A clear path to predictable, AI-powered growth.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://aihpower.com/best-of-best-listing" target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary text-lg px-10 py-6">
                  Apply for Co-Marketing Support
                  <ExternalLink className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <Link to="/submit-business">
                <Button variant="outline" className="btn-secondary text-lg px-10 py-6">
                  Create My Free Listing
                  <Zap className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === ACTIVE BEST OF BEST LISTING BENEFITS === */}
      <ListingBenefitsSection
        title="What Every Active Best of Best Listing Can Include"
        subtitle="More than a directory listing — a complete local lead engine. Here's what comes with an active Best of Best Listing on LocalBest.ai."
        showCta={false}
      />

      {/* What You Get Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
              What You Get as a <span className="gradient-text">Best of Best</span> Business
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
              You don't just get listed. You get a complete AI-powered business growth system.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {whatYouGet.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-6 rounded-2xl group hover:border-[#7C3AED]/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <item.icon className="w-6 h-6 text-[#7C3AED]" />
                </div>
                <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-2">
                  {item.title}
                </h3>
                <p className="text-[#94A3B8] text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Promotion Channels */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 rounded-2xl text-center"
          >
            <h3 className="text-white font-semibold font-['Outfit'] text-xl mb-6">
              Your Business Gets Promoted Across:
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {promotionChannels.map((channel, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10"
                >
                  <channel.icon className="w-4 h-4 text-[#00E5FF]" />
                  <span className="text-white text-sm">{channel.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI Business Hero Image Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#0a0f1e] to-[#020817]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-2xl overflow-hidden neon-glow-cyan"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_aisearchhub-1/artifacts/e3qvtwq5_ChatGPT%20Image%20Feb%204%2C%202026%2C%2010_25_42%20AM.png"
              alt="AI Business Heroes Team"
              className="w-full h-auto object-cover rounded-2xl"
            />
          </motion.div>
        </div>
      </section>

      {/* AI Business Hero Capabilities */}
      <section className="py-20 md:py-32 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/20 mb-6">
              <Bot className="w-4 h-4 text-[#7C3AED]" />
              <span className="text-sm text-[#7C3AED]">Not a Chatbot — A Growth Engine</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
              What Your <span className="gradient-text">AI Business Hero™</span> Does
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-3xl mx-auto">
              Your AI Business Hero™ represents your company everywhere your customers are. 
              It promotes, speaks, sells, and manages — all automatically.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {heroCapabilities.map((cap, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-6 rounded-2xl"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${cap.color}20` }}
                  >
                    <cap.icon className="w-5 h-5" style={{ color: cap.color }} />
                  </div>
                  <h3 className="text-white font-semibold font-['Outfit'] text-lg">
                    {cap.category}
                  </h3>
                </div>
                <ul className="space-y-2">
                  {cap.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#94A3B8]">
                      <CheckCircle2 className="w-4 h-4 text-[#00E5FF] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
              How It <span className="gradient-text">Works</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto">
              Your AI Business Hero™ connects directly to your entire business ecosystem.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-4 mb-12">
            {[
              { icon: Zap, label: "LocalBest.ai Platform" },
              { icon: Users, label: "Your CRM" },
              { icon: Calendar, label: "Your Calendar" },
              { icon: Megaphone, label: "Marketing Systems" },
              { icon: TrendingUp, label: "Sales Systems" },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-4 rounded-xl text-center"
              >
                <item.icon className="w-8 h-8 text-[#00E5FF] mx-auto mb-2" />
                <p className="text-white text-sm font-medium">{item.label}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 rounded-2xl text-center max-w-3xl mx-auto"
          >
            <p className="text-xl text-[#E2E8F0] mb-6">
              When someone visits your directory listing, they don't just see your business.
            </p>
            <p className="text-2xl font-bold font-['Outfit'] text-white">
              They meet your <span className="gradient-text">AI Business Hero™</span> —
              <br />your digital spokesperson and sales engine working 24/7.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hero Video Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#020817] to-[#0a0f1e]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-2xl overflow-hidden neon-glow-purple"
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-auto object-cover"
              poster="https://customer-assets.emergentagent.com/job_aisearchhub-1/artifacts/phhh0xgx_ChatGPT%20Image%20Feb%204%2C%202026%2C%2004_10_58%20PM.png"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_aisearchhub-1/artifacts/vgkl626y_kling_20260205_Image_to_Video_Epic_cinem_1596_0.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
            {/* Gradient overlay at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#0a0f1e] to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* SEO Benefits Section */}
      <section className="py-20 md:py-32" data-testid="seo-benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 mb-6">
              <Search className="w-4 h-4 text-[#10B981]" />
              <span className="text-sm text-[#10B981]">SEO Power</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
              Why Your Listing <span className="gradient-text">Boosts Your SEO</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-3xl mx-auto">
              Your directory listing is not just exposure — it is a powerful SEO asset designed to help your business rank higher and get found by customers and AI search engines.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Built for Search Engines */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-6 rounded-2xl"
            >
              <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/20 flex items-center justify-center mb-4">
                <Brain className="w-6 h-6 text-[#7C3AED]" />
              </div>
              <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-3">
                Built for Google & AI Search
              </h3>
              <p className="text-[#94A3B8] text-sm">
                Every listing is structured so Google, Bing, Apple Maps, ChatGPT, and voice assistants can understand and recommend your business.
              </p>
            </motion.div>

            {/* Local SEO Signals */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card p-6 rounded-2xl"
            >
              <div className="w-12 h-12 rounded-xl bg-[#00E5FF]/20 flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-[#00E5FF]" />
              </div>
              <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-3">
                Strong Local SEO (NAP)
              </h3>
              <p className="text-[#94A3B8] text-sm">
                Your Name, Address, Phone, Services — all formatted with structured data. Creates a local citation that builds trust with Google.
              </p>
            </motion.div>

            {/* High-Quality Backlink */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 rounded-2xl"
            >
              <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center mb-4">
                <Link2 className="w-6 h-6 text-[#F59E0B]" />
              </div>
              <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-3">
                High-Quality Backlink
              </h3>
              <p className="text-[#94A3B8] text-sm">
                Direct link to your website from a niche-relevant directory. Increases domain authority and helps your site rank higher.
              </p>
            </motion.div>

            {/* More Visibility */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 rounded-2xl"
            >
              <div className="w-12 h-12 rounded-xl bg-[#10B981]/20 flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-[#10B981]" />
              </div>
              <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-3">
                More Places to Be Found
              </h3>
              <p className="text-[#94A3B8] text-sm">
                Even if your website is not ranking, your directory listing can show up in search results — another digital property working for you.
              </p>
            </motion.div>

            {/* Trust & Credibility */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 rounded-2xl"
            >
              <div className="w-12 h-12 rounded-xl bg-[#EC4899]/20 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-[#EC4899]" />
              </div>
              <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-3">
                Trust & Credibility
              </h3>
              <p className="text-[#94A3B8] text-sm">
                Being listed in a verified directory establishes online legitimacy, brand presence, and local authority — trust converts to customers.
              </p>
            </motion.div>

            {/* Future-Proof */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="glass-card p-6 rounded-2xl"
            >
              <div className="w-12 h-12 rounded-xl bg-[#6366F1]/20 flex items-center justify-center mb-4">
                <Rocket className="w-6 h-6 text-[#6366F1]" />
              </div>
              <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-3">
                Future of Search Ready
              </h3>
              <p className="text-[#94A3B8] text-sm">
                Optimized for AI-powered search, voice search, and structured data schema — visible today and positioned for tomorrow.
              </p>
            </motion.div>
          </div>

          {/* SEO Advantage Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 rounded-2xl border border-[#10B981]/30 text-center"
          >
            <h3 className="text-xl font-bold font-['Outfit'] text-white mb-4">
              Your Listing = Permanent SEO Asset
            </h3>
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              {["SEO-optimized", "AI-search friendly", "Local-map compatible", "Backlink generating"].map((item, idx) => (
                <span key={idx} className="px-3 py-1 rounded-full bg-[#10B981]/10 text-[#10B981] text-sm border border-[#10B981]/30">
                  {item}
                </span>
              ))}
            </div>
            <p className="text-[#94A3B8]">
              Think of it as a digital billboard in search results and a second website working for you 24/7.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Retargeting Pixels Section with Video */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#020817] via-[#0a0f1e] to-[#020817] relative overflow-hidden" data-testid="retargeting-section">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#F59E0B]/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#7C3AED]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Video with Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto mb-8"
          >
            <div className="relative rounded-2xl overflow-hidden neon-glow-orange">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-auto object-cover cursor-pointer"
                title="Click to toggle sound"
                onClick={(e) => {
                  e.target.muted = !e.target.muted;
                }}
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/ce0ho5l9_kling_20260206_Image_to_Video_Epic_cinem_446_0.mp4" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
              {/* Badge attached to bottom of video */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/90 border border-[#F59E0B] backdrop-blur-sm shadow-lg">
                  <Target className="w-4 h-4 text-white" />
                  <span className="text-sm text-white font-medium">Multi-Channel Retargeting</span>
                </div>
              </div>
              {/* Gradient overlay at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0a0f1e] to-transparent" />
            </div>
          </motion.div>

          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] text-white mb-4">
              Turn One Visit Into <span className="text-[#F59E0B]">Everywhere Visibility</span>
            </h2>
            <p className="text-[#94A3B8] text-lg max-w-3xl mx-auto mb-6">
              Retargeting pixels are small pieces of tracking technology that remember when someone visits your business online — 
              so your brand can continue showing up after they leave.
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-[#E2E8F0]">
              <span className="text-sm">Most businesses rely on a single pixel on their website.</span>
              <span className="hidden md:block text-[#F59E0B]">→</span>
              <span className="text-sm font-semibold text-[#F59E0B]">LocalBest.ai listings go much further.</span>
            </div>
          </motion.div>

          {/* Retargeting Channels Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {/* Social Media */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-6 rounded-2xl border border-[#3b5998]/30 hover:border-[#3b5998]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#3b5998] to-[#E1306C] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Smartphone className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Social Media (Meta)</h4>
              <p className="text-[#94A3B8] text-sm">
                Your ads follow them on Facebook, Instagram, Reels, Stories, and more.
              </p>
            </motion.div>

            {/* Search & Display */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card p-6 rounded-2xl border border-[#4285F4]/30 hover:border-[#4285F4]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#4285F4] to-[#34A853] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Monitor className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Search & Display (Google)</h4>
              <p className="text-[#94A3B8] text-sm">
                They see your brand when they search online, browse websites, or watch YouTube.
              </p>
            </motion.div>

            {/* Premium TV */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 rounded-2xl border border-[#7C3AED]/30 hover:border-[#7C3AED]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Tv className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Premium TV Advertising</h4>
              <p className="text-[#94A3B8] text-sm">
                Your commercial appears across <span className="text-[#7C3AED] font-semibold">500+ premium TV stations</span>.
              </p>
            </motion.div>

            {/* Radio */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 rounded-2xl border border-[#F59E0B]/30 hover:border-[#F59E0B]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#EF4444] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Volume2 className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Radio Advertising</h4>
              <p className="text-[#94A3B8] text-sm">
                Your message plays across <span className="text-[#F59E0B] font-semibold">1,000+ radio stations</span>.
              </p>
            </motion.div>

            {/* Direct Mail */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 rounded-2xl border border-[#10B981]/30 hover:border-[#10B981]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Send className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">First-Class Direct Mail</h4>
              <p className="text-[#94A3B8] text-sm">
                Professionally designed postcards delivered to their mailbox with QR tracking.
              </p>
            </motion.div>

            {/* Email & Nurture */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="glass-card p-6 rounded-2xl border border-[#00E5FF]/30 hover:border-[#00E5FF]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#0EA5E9] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Mail className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Email & Nurture Campaigns</h4>
              <p className="text-[#94A3B8] text-sm">
                Prospects receive follow-up emails with coupons, content, and promotions.
              </p>
            </motion.div>

            {/* Content Retargeting */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="glass-card p-6 rounded-2xl border border-[#EC4899]/30 hover:border-[#EC4899]/60 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#EC4899] to-[#F43F5E] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Content Retargeting</h4>
              <p className="text-[#94A3B8] text-sm">
                Blogs, pages, offers, and promotions continue retargeting the same audience.
              </p>
            </motion.div>

            {/* Additional Platforms */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="glass-card p-6 rounded-2xl border border-white/10 hover:border-white/30 transition-all group"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Globe className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-semibold font-['Outfit'] text-lg mb-2">Additional Platforms</h4>
              <p className="text-[#94A3B8] text-sm">
                TikTok, LinkedIn, X (Twitter), and more — expanding reach everywhere.
              </p>
            </motion.div>
          </div>

          {/* Why This Is Different */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 md:p-12 rounded-2xl border-2 border-[#F59E0B]/30 text-center"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#EF4444] flex items-center justify-center">
                <Repeat className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white">
                Why This Is Different
              </h3>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-8">
              <div className="text-center">
                <p className="text-[#94A3B8] text-lg mb-2">Most directories stop at</p>
                <p className="text-2xl font-bold text-white">Exposure</p>
              </div>
              <div className="text-4xl text-[#F59E0B]">→</div>
              <div className="text-center">
                <p className="text-[#94A3B8] text-lg mb-2">LocalBest.ai listings activate</p>
                <p className="text-2xl font-bold text-[#F59E0B]">Everywhere Visibility</p>
              </div>
            </div>
            <div className="grid md:grid-cols-4 gap-4 mb-8 max-w-4xl mx-auto">
              {[
                { text: "See your brand repeatedly", icon: Eye },
                { text: "Recognize your name", icon: Star },
                { text: "Feel familiar and confident", icon: Shield },
                { text: "Believe you're established", icon: Award }
              ].map((item, idx) => (
                <div key={idx} className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-[#F59E0B]" />
                  </div>
                  <span className="text-[#E2E8F0] text-sm text-center">{item.text}</span>
                </div>
              ))}
            </div>
            <div className="bg-[#0a0f1e] rounded-xl p-6 max-w-2xl mx-auto">
              <p className="text-xl md:text-2xl font-bold text-white mb-2">
                They see your listing once —
              </p>
              <p className="text-xl md:text-2xl font-bold text-[#F59E0B]">
                then they see your ads everywhere.
              </p>
              <p className="text-lg text-[#10B981] mt-4 font-semibold">
                That&apos;s AI Horse-Power.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-20 md:py-32 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-6">
                Why This <span className="gradient-text">Matters</span>
              </h2>
              <p className="text-[#94A3B8] text-lg mb-8">
                Most businesses struggle with:
              </p>
              <div className="space-y-3 mb-8">
                {[
                  "Missing calls",
                  "Losing leads",
                  "Forgetting follow-ups",
                  "Inconsistent marketing",
                  "Not tracking performance",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-[#94A3B8]">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-xl text-white font-semibold font-['Outfit']">
                Your AI Business Hero™ never sleeps.
              </p>
              <p className="text-[#00E5FF] text-lg mt-4">
                It works 24/7 — Promoting • Selling • Booking • Managing • Reporting
              </p>
              <p className="text-white text-xl mt-6 font-['Outfit'] font-medium">
                So you can focus on running your business.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 rounded-2xl neon-glow-purple"
            >
              <h3 className="text-2xl font-bold font-['Outfit'] text-white mb-6">
                Ready to Get Started?
              </h3>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[#00E5FF]" />
                  <span className="text-white">Up to $120,000 in Co-Op Funding</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[#00E5FF]" />
                  <span className="text-white">Your Own AI Business Hero™</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[#00E5FF]" />
                  <span className="text-white">LocalBest.ai Platform & CRM</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[#00E5FF]" />
                  <span className="text-white">Multi-Channel Promotion</span>
                </div>
              </div>
              <div className="space-y-3">
                <a href="https://aihpower.com/best-of-best-listing" target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full btn-primary py-6 text-lg">
                    Get $120K Funding Now
                    <ExternalLink className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <a href="https://localbest.ai" target="_blank" rel="noopener noreferrer" className="block">
                  <Button variant="outline" className="w-full btn-secondary py-6">
                    Visit LocalBest.ai
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Simple CTA */}
      <section className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-10 rounded-2xl border border-[#F59E0B]/30"
          >
            <DollarSign className="w-16 h-16 text-[#F59E0B] mx-auto mb-6" />
            <h2 className="text-2xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
              Simple Version for Business Owners
            </h2>
            <p className="text-xl text-[#E2E8F0] mb-2">
              You don't just get listed.
            </p>
            <p className="text-2xl font-bold text-white mb-6">
              You get an <span className="gradient-text">AI Business Hero™</span> working for you.
            </p>
            <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto mb-8">
              An AI that markets your business, books your appointments, manages your leads, 
              follows up with customers, and helps close more deals.
            </p>
            <a href="https://aihpower.com/best-of-best-listing" target="_blank" rel="noopener noreferrer">
              <Button className="btn-primary text-lg px-12 py-6">
                Start Your Path to Funding
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
